import {
  EuiButtonIcon,
  EuiContextMenu,
  EuiContextMenuPanelDescriptor,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { memo, useMemo, useState, useCallback } from "react";
import { css } from "@emotion/react";
import { isDefined } from "utils/isDefined";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { Group, IdeasFile } from "@inscopix/ideas-hyperformula";
import { DataTableRowData } from "pages/gdt/layout/DataTable";
import { MetadataTableFileGdt } from "./MetadataTableFileGdt";
import { getColumnAddress } from "pages/gdt/column-headers/ColumnHeaderBase";
import { DATA_TABLE_TOOLTIP_ID } from "pages/gdt/layout/DataTableTooltip";

export interface MetadatumReferenceSelectorGdtProps {
  rowId: DataTableRowData["id"];
  tableId: string;
  columnId: string;
  onPopoverOpen?: () => void;
  onPopoverClose?: () => void;
  currentReference:
    | {
        tableId: string;
        rowId: string;
        columnId: string;
        metadataKey: string;
        value: string | number | boolean | null;
      }
    | undefined;
  isReadonly: boolean;
}

/** Component for selecting file and recording metadata in analysis table rows */
export const MetadatumReferenceSelectorGdt = memo(
  function MetadatumReferenceSelectorGdt({
    rowId,
    tableId,
    columnId,
    onPopoverOpen,
    onPopoverClose,
    currentReference,
    isReadonly,
  }: MetadatumReferenceSelectorGdtProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    /** Opens the popover */
    const openPopover = () => {
      setIsPopoverOpen(true);
      if (isDefined(onPopoverOpen)) {
        onPopoverOpen();
      }
    };

    /** Closes the popover */
    const closePopover = useCallback(() => {
      setIsPopoverOpen(false);
      if (isDefined(onPopoverClose)) {
        onPopoverClose();
      }
    }, [onPopoverClose]);

    /** Opens the popover if it is closed, closes the popover if it is open */
    const togglePopover = isPopoverOpen ? closePopover : openPopover;
    const setCellFormula = useDataTableContext((s) => s.setCellFormula);
    const currentTable = useDataTableContext((s) =>
      s.tables.find((table) => table.id === tableId),
    );

    const fileColumns = useDataTableContext(
      (s) =>
        s.tables
          .find((table) => table.id === s.selectedTableId)
          ?.columns // .map((col, index) => ({ ...col, index }))
          .filter((col) => !col.is_result)
          .filter((col) => col.definition.kind === "file"),
    );

    const currentRow = currentTable?.rows
      .map((row, index) => ({ ...row, index }))
      .find(({ id }) => id === rowId);

    const { panels, initialPanelId } = useMemo(() => {
      /** Panels for selecting file metadata */
      const toolPathParamPanels: EuiContextMenuPanelDescriptor[] = (
        fileColumns ?? []
      ).map((fileCol) => ({
        id: fileCol.id,
        title: fileCol.name,
        content: (() => {
          const handleSelect = (
            metadatumKey: string,
            value: string | number | boolean | null,
          ) => {
            if (!isReadonly) {
              const colAddr = getColumnAddress(fileCol.order);
              void setCellFormula({
                address: { tableId, columnId, rowId },
                formula: `=METADATUM(${colAddr}:${colAddr},"${metadatumKey}",${
                  typeof value === "string"
                    ? `"${String(value)}"`
                    : String(value)
                })`,
              });

              closePopover();
            }
          };

          const cellValue = currentRow?.cells[fileCol.order].value;

          const fileIds =
            cellValue instanceof Group
              ? cellValue.cellValues.flatMap((val) =>
                  val instanceof IdeasFile ? val.attrs.id : [],
                )
              : cellValue instanceof IdeasFile
              ? [cellValue.attrs.id]
              : [];

          return (
            <MetadataTableFileGdt
              fileIds={fileIds}
              onSelect={handleSelect}
              currentReference={currentReference}
            />
          );
        })(),
      }));
      /** Panel that is initially visible when the popover is opened */
      const rootPanel: EuiContextMenuPanelDescriptor = {
        id: "root",
        title: "Fill cell from file metadata",
        items: [
          ...toolPathParamPanels.map((panel) => ({
            panel: panel.id,
            name: panel.title,
            icon: "documents",
          })),
        ],
      };
      const initialPanelId = (() => {
        const panel = toolPathParamPanels.find(
          (panel) => panel.id === currentReference?.columnId,
        );
        return panel?.id ?? rootPanel.id;
      })();
      return { panels: [rootPanel, ...toolPathParamPanels], initialPanelId };
    }, [
      closePopover,
      columnId,
      currentReference,
      currentRow?.cells,
      fileColumns,
      isReadonly,
      rowId,
      setCellFormula,
      tableId,
    ]);

    return (
      <EuiPopover
        button={
          <EuiButtonIcon
            aria-label="Fill cell from file metadata"
            iconType="link"
            onClick={togglePopover}
            data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
            data-tooltip-html={"Fill cell from file metadata"}
          />
        }
        isOpen={isPopoverOpen}
        closePopover={closePopover}
        panelPaddingSize="none"
        anchorPosition="leftCenter"
      >
        <EuiContextMenu
          initialPanelId={initialPanelId}
          panels={panels}
          css={css`
            inline-size: 500px;
          `}
        />
      </EuiPopover>
    );
  },
);
